package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.IconSize
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.shared.ui.TooltipWarm
import co.maytes.standupbot.shared.ui.TooltipWarmState
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.onBlur
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onFocus
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.onMouseDown
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.jetbrains.compose.web.dom.Button as ButtonElement

internal object TooltipWarmClock {
    var state = TooltipWarmState()
}

internal class TooltipController {
    var open by mutableStateOf(false)
        private set
    private var timer: Int? = null

    fun requestOpen() {
        if (open || timer != null) return
        val delay = TooltipWarm.delayMs(TooltipWarmClock.state, nowMs())
        if (delay == 0L) {
            show()
        } else {
            timer = window.setTimeout({
                timer = null
                show()
            }, delay.toInt())
        }
    }

    fun hide() {
        timer?.let { window.clearTimeout(it) }
        timer = null
        if (open) {
            open = false
            TooltipWarmClock.state = TooltipWarm.closed(TooltipWarmClock.state, nowMs())
        }
    }

    private fun show() {
        if (open) return
        open = true
        TooltipWarmClock.state = TooltipWarm.opened(TooltipWarmClock.state)
    }

    private fun nowMs(): Long = window.performance.now().toLong()
}

@Composable
internal fun rememberTooltipController(): TooltipController {
    val controller = remember { TooltipController() }
    DisposableEffect(controller) { onDispose { controller.hide() } }
    return controller
}

internal fun isKeyboardFocus(element: HTMLElement?): Boolean =
    try {
        element?.matches(":focus-visible") ?: false
    } catch (_: Throwable) {
        true
    }

@Composable
fun IconButton(
    ariaLabel: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    size: ControlSize = ControlSize.Md,
    loading: Boolean = false,
    tooltip: String? = ariaLabel,
    tooltipPlacement: TooltipPlacement = TooltipPlacement.Bottom,
    onClick: () -> Unit,
    content: @Composable () -> Unit,
) {
    val interactive = enabled && !loading
    val controller = rememberTooltipController()
    val side = size.metrics.height

    Div(
        attrs = Modifier
            .styleModifier {
                property("position", "relative")
                property("display", "inline-flex")
                property("flex-shrink", "0")
            }
            .onMouseEnter { if (tooltip != null) controller.requestOpen() }
            .onMouseLeave { controller.hide() }
            .toAttrs(),
    ) {
        ButtonElement(
            attrs = Modifier
                .classNames(ControlClass.ICON_BUTTON)
                .width(side)
                .height(side)
                .let { if (interactive) it.pressable() else it }
                .onClick { event ->
                    controller.hide()
                    if (interactive) onClick() else event.preventDefault()
                }
                .onMouseDown { event -> if (!enabled) event.preventDefault() }
                .onFocus { event ->
                    if (tooltip != null && isKeyboardFocus(event.target as? HTMLElement)) controller.requestOpen()
                }
                .onBlur { controller.hide() }
                .onKeyDown { event -> if (event.key == "Escape") controller.hide() }
                .then(modifier)
                .attrsModifier {
                    attr("type", "button")
                    attr("aria-label", ariaLabel)
                    if (!enabled) attr("aria-disabled", "true")
                    if (loading) attr("aria-busy", "true")
                }
                .toAttrs(),
        ) {
            if (loading) {
                IconSlot {
                    Spinner(sizePx = IconSize.sm.value.toInt(), color = Palette.textSecondary, ariaLabel = "")
                }
            } else {
                content()
            }
        }
        if (tooltip != null) {
            TooltipBubble(
                text = tooltip,
                placement = tooltipPlacement,
                open = controller.open,
                modifier = Modifier.attrsModifier { attr("aria-hidden", "true") },
            )
        }
    }
}
